/**
 * Report UI Transform events for the grids (spec 094).
 *
 * ── Why this wraps HANDLERS, not the preferencesStore mutators ──────────────
 *
 * The reference implementation says to wrap the `preferencesStore` mutators, so
 * "all three grids are covered once". Re-resolved against this tree, that seam
 * produces wrong numbers, for four separate reasons:
 *
 *  1. `setColumnWidths` is written from an effect fed by TanStack with
 *     `columnResizeMode: 'onChange'` (DataGrid.jsx), so it fires on EVERY
 *     mouse-move of a resize drag.
 *  2. Unfreezing a pinned column writes 1..N `setPageFlag` calls in a loop
 *     (`pinFlagsToClearOnUnfreeze`) for ONE user action.
 *  3. Applying a saved view writes visibility + widths + order + sorting and N
 *     alignments — again one user action, many mutator calls.
 *  4. The `setSorting` effects carry deliberate mount-skip refs
 *     (`sortingPersistedOnce`) because persisting an unchosen metadata default
 *     once read as a user choice (CC-49948). A store-level wrapper sits below
 *     those guards and would re-introduce exactly that bug in the analytics.
 *
 * `trackTransform`'s debounce would mask (1) into one event, but nothing can
 * recover (2), (3) or (4) — they are miscounts, not bursts. So events are
 * emitted at the discrete user action instead.
 *
 * The hard constraint from the brief is honoured either way: NO method is added
 * to `preferencesStore`. It is referenced by ~46 test files, ~42 of which mock
 * it, and a new method has to be added to every one of those mocks. This module
 * is the separate wrapper, following the `columnPinning.js` precedent of
 * shipping a feature without touching the shared store's surface.
 *
 * ── Why the storageKey gate is not optional ─────────────────────────────────
 *
 * `DataGrid` has ELEVEN consumers and only one is reports — it is also the
 * Matters, Contacts, Invoices, Billing, Payments, PreBills, Objects,
 * ObjectList, PaymentDetail and Report Queue grid. Emitting from its handlers
 * ungated would file "hid a column on the Matters list" as a Report UI
 * Transform and quietly corrupt every per-report number.
 *
 * Every report grid's storageKey is prefixed `report:` (ReportView builds
 * `report:${actionKey || reportId}`; the specialized pages use slugs such as
 * `report:balance-sheet`), and no non-report consumer uses that prefix — they
 * pass bare keys like `matters`. That prefix is the gate.
 */

import { trackTransform } from './pendo'

const REPORT_PREFIX = 'report:'

/** True only for a report grid's storage key. See the header note. */
export const isReportStorageKey = (storageKey) =>
  String(storageKey ?? '').startsWith(REPORT_PREFIX)

/**
 * The report identifier carried on transform events.
 *
 * Generic reports yield the NUMERIC report id, which joins straight to the run
 * events' `reportId`. The specialized pages use a stable slug
 * (`report:balance-sheet`), which does not join numerically — recorded as a
 * known limitation in spec 094 rather than papered over with a lookup that
 * could go stale.
 */
export function reportRefFromStorageKey(storageKey) {
  if (!isReportStorageKey(storageKey)) return null
  const ref = String(storageKey).slice(REPORT_PREFIX.length)
  return /^\d+$/.test(ref) ? Number(ref) : ref
}

/**
 * Emit one coalesced Report UI Transform for a report grid.
 *
 * No-ops for a non-report grid, and no-ops entirely when Pendo is disabled
 * (`trackTransform` gates on the API key), so this is safe to call from any
 * shared handler.
 *
 * @param {string} storageKey the grid's per-page storage key
 * @param {string} transform  the transform enum token
 * @param {string} [detail]   a low-cardinality token. NEVER a filter value, a
 *                            cell value, or a column label a firm renamed —
 *                            column IDs and enum tokens only.
 */
export function trackGridTransform(storageKey, transform, detail) {
  const reportId = reportRefFromStorageKey(storageKey)
  if (reportId == null) return
  trackTransform({ transform, reportId, detail })
}
